import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { filter } from 'rxjs';

import { environment } from '../../../environments/environment';
import { GlobalService } from '../global.service';
import { LocalStorageService } from '../local-storage.service';
import { ConnectionStatus, NetworkService } from '../network.service';
import { versionInfo } from '../../../../version-info';
import { getOrCreateDeviceId } from './device-id.util';
import { decodeJwtSub } from './jwt-sub.util';
import {
  clearOfflineQueue,
  enqueueOfflineItems,
  OfflineQueueEntry,
  readOfflineQueue,
} from './offline-queue.util';
import {
  ErroreClientApp,
  ErroreClientBatchRequest,
  ErroreClientItem,
  ErroreClientPiattaforma,
} from './error-reporting.models';
import {
  BATCH_SIZE_MAX,
  BATCH_SIZE_MAX_BYTES,
  DEDUP_WINDOW_MS,
  ERRORI_CLIENT_PATH,
  FLUSH_INTERVAL_MS,
  MAX_DISTINCT_KEYS,
  RIPETIZIONI_MAX,
} from './error-reporting.constants';

/** Campi di un elemento del batch, tranne quelli che il dedup calcola da solo (istante/ripetizioni). */
type ItemBase = Omit<ErroreClientItem, 'occurred_at' | 'ripetizioni'>;

/** Un elemento pronto per l'invio, con l'identità di chi lo ha generato (vedi {@link OfflineQueueEntry}). */
type QueueEntry = OfflineQueueEntry;

interface BufferEntry {
  base: ItemBase;
  /**
   * `sub` del JWT presente quando è iniziata questa finestra (null se anonimo): NON viene
   * riletto a ogni ripetizione, per evitare che un cambio di sessione a metà finestra attribuisca
   * l'intera finestra all'utente sbagliato (CORREZIONI-GIRO-2, "Mobile", punto 2).
   */
  sub: string | null;
  /**
   * Inizio della finestra di dedup di {@link DEDUP_WINDOW_MS}. Il flush a {@link FLUSH_INTERVAL_MS}
   * NON la azzera (CORREZIONI-GIRO-2, "Modifiche di contratto" — "Deduplica vera a 60s"): resta
   * fissa finché la finestra non scade.
   */
  windowStart: number;
  /** Istante della PRIMA occorrenza della finestra; `null` quando è già stata inviata. */
  firstOccurredAt: number | null;
  /** Ripetizioni successive alla prima, accumulate nella finestra corrente, non ancora inviate. */
  accumCount: number;
  /** Ultimo istante visto nella finestra: diventa l'`occurred_at` del bundle di ripetizioni. */
  lastSeenAt: number;
}

/**
 * Raccoglie eccezioni JS e submit bloccati dalla validazione e li invia a
 * `POST /api/v1/errori-client` (vedi `reviews/errori-client/SPEC.md` e
 * `reviews/errori-client/CORREZIONI-GIRO-2.md`).
 *
 * Regole non negoziabili (vedi SPEC, sezione "Client" e "Mobile"):
 * - MAI passare dagli interceptor HTTP: usa `HttpClient` costruito su `HttpBackend`. Un 401 gestito
 *   dal `RefreshTokenInterceptor` farebbe logout/redirect su un endpoint pubblico — inaccettabile.
 * - MAI propagare un'eccezione: ogni metodo pubblico è avvolto in try/catch, e gli errori di invio
 *   sono ignorati in silenzio (nessun `console.error`, che rientrerebbe nell'`ErrorHandler`).
 * - Versione app e piattaforma si leggono al momento dell'INVIO, non della raccolta: il bridge
 *   nativo può valorizzarle dopo il primo errore.
 * - Timer e listener globali (flush periodico, rete, visibilità) girano FUORI dalla zona Angular
 *   (`NgZone.runOutsideAngular`): non devono innescare change detection ogni 5s.
 *
 * Deduplica vera a {@link DEDUP_WINDOW_MS}: la prima occorrenza di una chiave parte al flush
 * successivo (entro {@link FLUSH_INTERVAL_MS}); le ripetizioni della stessa chiave nel resto della
 * finestra si accumulano e partono come UN solo elemento (`ripetizioni = n`, `occurred_at` =
 * ultima volta vista) al primo flush dopo la scadenza della finestra.
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportingService implements OnDestroy {
  private readonly http: HttpClient;
  private readonly deviceId = getOrCreateDeviceId();
  private readonly buffer = new Map<string, BufferEntry>();
  /** Finestre chiuse (scadute) tra un flush e l'altro, in attesa del PROSSIMO flush. */
  private pendingClosed: QueueEntry[] = [];
  private readonly flushTimer: ReturnType<typeof setInterval>;
  private readonly visibilityHandler = () => {
    try {
      if (typeof document === 'undefined') {
        return;
      }
      if (document.visibilityState === 'hidden') {
        // L'app va in background (o la WebView può essere sospesa/uccisa da un momento all'altro):
        // quanto è in sospeso in memoria si persiste SUBITO, non si aspetta il prossimo flush.
        this.moveBufferedItemsToOfflineQueue();
      } else if (document.visibilityState === 'visible') {
        this.flushOfflineQueue();
      }
    } catch {
      // Un listener globale non deve MAI propagare.
    }
  };
  private flushingOffline = false;

  constructor(
    httpBackend: HttpBackend,
    private readonly router: Router,
    private readonly globalService: GlobalService,
    private readonly localStorageService: LocalStorageService,
    private readonly networkService: NetworkService,
    private readonly ngZone: NgZone,
  ) {
    this.http = new HttpClient(httpBackend);

    // Boot: prova subito a svuotare quanto è rimasto da una sessione precedente.
    this.flushOfflineQueue();

    // Timer e listener SEMPRE fuori dalla zona Angular: non sono legati a nessuna UI e non devono
    // innescare change detection a ogni tick/evento.
    this.flushTimer = this.ngZone.runOutsideAngular(() => {
      // Ritorno della rete: NetworkService emette solo sui cambi di stato (window online/offline).
      this.networkService.getStatusChangeObservable$()
        .pipe(filter((status): status is ConnectionStatus => !!status?.connected))
        .subscribe(() => {
          try {
            this.flushOfflineQueue();
          } catch {
            // Mai propagare da una callback fuori zona.
          }
        });

      // Ritorno dell'app in primo piano (o passaggio in background): questo progetto non usa il
      // plugin Capacitor App (assente dal package.json), quindi il segnale più vicino disponibile
      // è la visibilità del documento.
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', this.visibilityHandler);
      }

      return setInterval(() => {
        try {
          this.flush();
        } catch {
          // Mai propagare da un timer: rientrerebbe nell'ErrorHandler ogni 5s.
        }
      }, FLUSH_INTERVAL_MS);
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.flushTimer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  /**
   * Segnala un'eccezione JS non gestita o una promise rifiutata. Esclude esplicitamente gli
   * `HttpErrorResponse`: sono già tracciati nell'audit chiamate (SPEC, "Decisioni chiuse").
   */
  reportJsError(error: unknown): void {
    try {
      if (error instanceof HttpErrorResponse) {
        return;
      }
      const { tipo, messaggio, stack } = extractErrorInfo(error);
      const primaRiga = firstUsefulStackLine(stack);
      const key = `JS_ERROR|${tipo}|${messaggio}|${primaRiga}`;
      this.enqueue(key, {
        origine: 'JS_ERROR',
        tipo: truncate(tipo, 200),
        messaggio: truncate(messaggio, 1000),
        stack: stack ? truncate(stack, 4000) : undefined,
        pagina: this.currentPagina(),
      });
    } catch {
      // Il reporter non deve MAI propagare: rientrerebbe nell'ErrorHandler.
    }
  }

  /**
   * Segnala un submit bloccato dalla validazione client. `campo` e `regola` non devono MAI
   * contenere il valore digitato (vedi {@link ../error-reporting/form-validation-errors.util}).
   */
  reportValidationError(form: string, campo: string, regola: string, comuneSlug?: string): void {
    try {
      const key = `VALIDAZIONE|${form}|${campo}|${regola}`;
      this.enqueue(key, {
        origine: 'VALIDAZIONE',
        form: truncate(form, 200),
        campo: truncate(campo, 200),
        regola: truncate(regola, 100),
        pagina: this.currentPagina(),
        comune_slug: comuneSlug ? truncate(comuneSlug, 150) : undefined,
      });
    } catch {
      // Il reporter non deve MAI propagare.
    }
  }

  // ---- Dedup + batching ----------------------------------------------------

  private enqueue(key: string, base: ItemBase): void {
    const now = Date.now();

    let entry = this.buffer.get(key);
    if (entry && now - entry.windowStart >= DEDUP_WINDOW_MS) {
      // La finestra è scaduta: la si chiude (i suoi dati pendenti partiranno al PROSSIMO flush,
      // "il flush a 5s non azzera le finestre" vale al contrario per la chiusura: la scadenza è
      // rilevata qui o al prossimo giro di flush(), non prima) e se ne apre una nuova.
      this.closeWindow(key, entry);
      entry = undefined;
    }

    if (!entry) {
      if (this.buffer.size >= MAX_DISTINCT_KEYS) {
        // Tetto di sicurezza per sessione: la chiave nuova viene scartata in silenzio.
        return;
      }
      entry = {
        base,
        sub: this.currentSub(),
        windowStart: now,
        firstOccurredAt: now,
        accumCount: 0,
        lastSeenAt: now,
      };
      this.buffer.set(key, entry);
    } else {
      entry.accumCount = Math.min(entry.accumCount + 1, RIPETIZIONI_MAX);
      entry.lastSeenAt = now;
    }

    if (this.countPendingItems() >= BATCH_SIZE_MAX) {
      this.flush();
    }
  }

  /** Elementi pronti per un invio immediato: finestre chiuse in attesa + prime occorrenze non ancora spedite. */
  private countPendingItems(): number {
    let count = this.pendingClosed.length;
    for (const entry of this.buffer.values()) {
      if (entry.firstOccurredAt !== null) count += 1;
    }
    return count;
  }

  /**
   * Chiude una finestra scaduta: se c'era una prima occorrenza mai inviata, o ripetizioni
   * accumulate, li mette da parte per il prossimo flush. Rimuove SEMPRE la entry dal buffer: una
   * nuova occorrenza della stessa chiave dopo la scadenza apre una finestra nuova (SPEC, "Il flush
   * a 5s non azzera le finestre" — ma la finestra stessa, una volta scaduta, non si riapre).
   */
  private closeWindow(key: string, entry: BufferEntry): void {
    this.buffer.delete(key);
    if (entry.firstOccurredAt !== null) {
      this.pendingClosed.push({
        item: {
          ...entry.base,
          occurred_at: new Date(entry.firstOccurredAt).toISOString(),
          ripetizioni: 1,
        },
        sub: entry.sub,
      });
    }
    if (entry.accumCount > 0) {
      this.pendingClosed.push({
        item: {
          ...entry.base,
          occurred_at: new Date(entry.lastSeenAt).toISOString(),
          ripetizioni: entry.accumCount,
        },
        sub: entry.sub,
      });
    }
  }

  private flush(): void {
    const now = Date.now();
    const ready: QueueEntry[] = [];

    for (const [key, entry] of [...this.buffer.entries()]) {
      if (now - entry.windowStart >= DEDUP_WINDOW_MS) {
        // Prima finestra a scadere trovata dal giro periodico: si chiude e parte in QUESTO flush
        // ("al primo flush dopo la scadenza della finestra").
        this.closeWindow(key, entry);
        continue;
      }
      if (entry.firstOccurredAt !== null) {
        ready.push({
          item: {
            ...entry.base,
            occurred_at: new Date(entry.firstOccurredAt).toISOString(),
            ripetizioni: 1,
          },
          sub: entry.sub,
        });
        entry.firstOccurredAt = null;
      }
    }

    ready.push(...this.pendingClosed);
    this.pendingClosed = [];

    if (!ready.length) {
      return;
    }
    this.sendReady(ready);
  }

  /**
   * Sposta nella coda offline tutto ciò che è in sospeso in memoria (finestre chiuse non ancora
   * spedite + prime occorrenze pendenti + ripetizioni accumulate finora, anche se la finestra non
   * è ancora scaduta): usato quando l'app va in background (`visibilitychange` → `hidden`), perché
   * da lì in poi la WebView può essere sospesa o uccisa senza preavviso.
   */
  private moveBufferedItemsToOfflineQueue(): void {
    const toQueue: QueueEntry[] = [...this.pendingClosed];
    this.pendingClosed = [];

    for (const [key, entry] of [...this.buffer.entries()]) {
      if (entry.firstOccurredAt !== null) {
        toQueue.push({
          item: {
            ...entry.base,
            occurred_at: new Date(entry.firstOccurredAt).toISOString(),
            ripetizioni: 1,
          },
          sub: entry.sub,
        });
      }
      if (entry.accumCount > 0) {
        toQueue.push({
          item: {
            ...entry.base,
            occurred_at: new Date(entry.lastSeenAt).toISOString(),
            ripetizioni: entry.accumCount,
          },
          sub: entry.sub,
        });
      }
      this.buffer.delete(key);
    }

    if (toQueue.length) {
      enqueueOfflineItems(toQueue);
    }
  }

  // ---- Coda offline ----------------------------------------------------------

  private flushOfflineQueue(): void {
    if (this.flushingOffline) {
      return;
    }
    const pending = readOfflineQueue();
    if (!pending.length) {
      return;
    }
    this.flushingOffline = true;
    try {
      clearOfflineQueue();
      this.sendReady(pending);
    } finally {
      this.flushingOffline = false;
    }
  }

  // ---- Invio HTTP (bypassa gli interceptor: HttpBackend) ---------------------

  /**
   * Instrada gli elementi pronti per l'invio: quelli il cui `sub` coincide con quello del token
   * CORRENTE partono con `Authorization` (se presente); tutti gli altri partono SENZA, anonimi
   * (CORREZIONI-GIRO-2, "Mobile", punto 2 — mai attribuire a un utente l'errore di un altro). Ogni
   * gruppo è poi suddiviso in batch da {@link BATCH_SIZE_MAX} elementi o {@link BATCH_SIZE_MAX_BYTES}.
   */
  private sendReady(entries: QueueEntry[]): void {
    const currentSub = this.currentSub();
    const matching: QueueEntry[] = [];
    const mismatched: QueueEntry[] = [];
    for (const entry of entries) {
      (entry.sub === currentSub ? matching : mismatched).push(entry);
    }
    for (const batch of chunkEntries(matching, BATCH_SIZE_MAX, BATCH_SIZE_MAX_BYTES)) {
      this.postBatch(batch, false);
    }
    for (const batch of chunkEntries(mismatched, BATCH_SIZE_MAX, BATCH_SIZE_MAX_BYTES)) {
      this.postBatch(batch, true);
    }
  }

  private postBatch(entries: QueueEntry[], forceAnonymous: boolean): void {
    if (!entries.length) {
      return;
    }
    try {
      const errori = entries.map((entry) => entry.item);
      const body: ErroreClientBatchRequest = {
        app: 'MOBILE' as ErroreClientApp,
        versione_app: this.resolveVersione(),
        piattaforma: this.resolvePiattaforma(),
        device_id: this.deviceId,
        errori,
      };
      const url = `${environment.servicesUrl}${ERRORI_CLIENT_PATH}`;
      const headers = this.buildHeaders(!forceAnonymous);
      this.http.post(url, body, { headers }).subscribe({
        next: () => {
          // 202 senza corpo: nulla da fare.
        },
        error: (err: unknown) => this.handleSendError(err, entries, forceAnonymous),
      });
    } catch {
      // Invocazione fallita prima ancora della subscribe (es. HttpClient malconfigurato):
      // meglio provare più tardi che perdere il segnale.
      enqueueOfflineItems(entries);
    }
  }

  private handleSendError(err: unknown, entries: QueueEntry[], forceAnonymous: boolean): void {
    try {
      const status = err instanceof HttpErrorResponse ? err.status : undefined;

      // Token scaduto su endpoint permitAll: il resource server risponde comunque 401. Si reinvia
      // UNA volta senza Authorization (SPEC, "Contratto HTTP"). Se l'invio era già anonimo
      // (forceAnonymous) non si ritenta una seconda volta.
      if (status === 401 && !forceAnonymous) {
        this.postBatch(entries, true);
        return;
      }

      // 413 (corpo troppo grande): si divide il batch a metà e si riprova ciascuna metà
      // (CORREZIONI-GIRO-2, "Modifiche di contratto"). Un elemento singolo che dà 413 si scarta:
      // non c'è altro da dividere, e non ha senso rispedirlo invariato.
      if (status === 413) {
        if (entries.length > 1) {
          const mid = Math.ceil(entries.length / 2);
          this.postBatch(entries.slice(0, mid), forceAnonymous);
          this.postBatch(entries.slice(mid), forceAnonymous);
        }
        return;
      }

      // 4xx diverso da 401/413/429 (es. 400 corpo malformato): non ha senso reinviarlo, il
      // payload non cambierà. Si scarta in silenzio.
      const isNonRetryable4xx = typeof status === 'number' && status >= 400 && status < 500
        && status !== 401 && status !== 429;
      if (isNonRetryable4xx) {
        return;
      }

      // Rete assente (status 0), 5xx, 429, o 401 già ritentato: si prova più tardi.
      enqueueOfflineItems(entries);
    } catch {
      // Mai propagare.
    }
  }

  private buildHeaders(includeAuth: boolean): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (includeAuth) {
      const token = this.localStorageService.getAccessToken();
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }
    return headers;
  }

  /** `sub` del token corrente, `null` se anonimo o assente/non decodificabile. */
  private currentSub(): string | null {
    return decodeJwtSub(this.localStorageService.getAccessToken());
  }

  // ---- Versione / piattaforma / pagina, risolte al momento dell'invio --------

  private resolveVersione(): string | undefined {
    const fromBridge = this.globalService.appVersion;
    const versione = fromBridge || versionInfo?.raw || versionInfo?.tag || versionInfo?.hash;
    return versione ? truncate(versione, 50) : undefined;
  }

  private resolvePiattaforma(): ErroreClientPiattaforma {
    const raw = (this.localStorageService.getPlatform() ?? '').toLowerCase();
    if (raw === 'android' || raw === 'huawei') return 'ANDROID';
    if (raw === 'ios') return 'IOS';
    return 'WEB';
  }

  private currentPagina(): string {
    try {
      const url = this.router.url ?? '';
      return url.split('?')[0].split('#')[0];
    } catch {
      return '';
    }
  }
}

// ---- Helper puri, senza stato -------------------------------------------------

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

/** Dimensione in byte UTF-8 del JSON serializzato (CORREZIONI-GIRO-2: "misurati in byte UTF-8"). */
function jsonByteSize(items: ErroreClientItem[]): number {
  const json = JSON.stringify(items);
  try {
    return new TextEncoder().encode(json).length;
  } catch {
    // Fallback difensivo: approssimazione per caratteri (non byte), meglio di un'eccezione.
    return json.length;
  }
}

/**
 * Suddivide gli elementi in batch che rispettano SIA il tetto di conteggio SIA quello in byte del
 * JSON serializzato di `errori` (CORREZIONI-GIRO-2, "Modifiche di contratto"). Un elemento singolo
 * che da solo supera già il tetto in byte forma comunque un batch a sé (non ulteriormente
 * divisibile qui: se il BE risponde 413 lo si scarta, vedi {@link ErrorReportingService.handleSendError}).
 */
function chunkEntries(entries: QueueEntry[], maxCount: number, maxBytes: number): QueueEntry[][] {
  const out: QueueEntry[][] = [];
  let current: QueueEntry[] = [];
  let currentItems: ErroreClientItem[] = [];

  for (const entry of entries) {
    const wouldExceedCount = current.length + 1 > maxCount;
    const wouldExceedBytes = current.length > 0
      && jsonByteSize([...currentItems, entry.item]) > maxBytes;
    if (current.length > 0 && (wouldExceedCount || wouldExceedBytes)) {
      out.push(current);
      current = [];
      currentItems = [];
    }
    current.push(entry);
    currentItems.push(entry.item);
  }
  if (current.length) {
    out.push(current);
  }
  return out;
}

/**
 * Estrae tipo/messaggio/stack da un valore d'errore qualsiasi. Gli `Error` sono il caso comune;
 * stringhe e oggetti arbitrari (rifiuto di una promise con `reject('boom')` o `reject({...})`)
 * vengono normalizzati in modo robusto invece di far fallire l'estrazione.
 */
function extractErrorInfo(error: unknown): { tipo: string; messaggio: string; stack?: string } {
  if (error instanceof Error) {
    return {
      tipo: error.name || 'Error',
      messaggio: error.message || String(error),
      stack: error.stack,
    };
  }
  if (typeof error === 'string') {
    return { tipo: 'Error', messaggio: error };
  }
  try {
    return { tipo: 'Error', messaggio: JSON.stringify(error) ?? String(error) };
  } catch {
    return { tipo: 'Error', messaggio: String(error) };
  }
}

/**
 * Prima riga di stack "utile" per l'impronta: la prima riga spesso ripete `Nome: messaggio`
 * (V8), quindi si preferisce la seconda quando esiste. La normalizzazione fine (riga:colonna,
 * hash dei chunk) è responsabilità del BE (SPEC, "Backend").
 */
function firstUsefulStackLine(stack: string | undefined): string {
  if (!stack) {
    return '';
  }
  const lines = stack.split('\n').map((l) => l.trim()).filter(Boolean);
  if (!lines.length) {
    return '';
  }
  return lines.length > 1 ? lines[1] : lines[0];
}
